import { useMemo, forwardRef, useImperativeHandle } from "react"
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card"
import { XCircle, PieChart } from "lucide-react"
import { OvertimeRequest } from "@/types"
import { OvertimeStatus } from "@/types/enums"
import ExportButton from "@/components/ui/ExportButton"
import DonutChart, { DonutChartData } from "@/components/ui/DonutChart"

interface RejectionReasonDonutChartProps {
  requests: OvertimeRequest[]
  title?: string
  isLoading?: boolean
  standalone?: boolean
  hasActiveFilters?: boolean
}

interface RejectionReasonDonutChartHandle {
  download: () => void;
}

const RejectionReasonDonutChart = forwardRef<RejectionReasonDonutChartHandle, RejectionReasonDonutChartProps>(
  function RejectionReasonDonutChart(
    { 
      requests, 
      title = "Rejection Reasons",
      isLoading = false,
      standalone = false,
      hasActiveFilters = false
    },
    ref
  ) {

  // Process rejection reason data from approver comments of rejected requests
  const rejectionData = useMemo(() => {
    if (!requests?.length) return [];

    // Filter for rejected requests only
    const rejectedRequests = requests.filter(request => request.status === OvertimeStatus.REJECTED);
    
    if (!rejectedRequests.length) return [];

    // Categorize rejection comments into common reasons
    const categorizeRejection = (comment: string | null): string => {
      if (!comment) return 'No reason provided';
      
      const text = comment.toLowerCase();
      
      if (text.includes('budget') || text.includes('cost') || text.includes('expensive')) {
        return 'Budget Constraints';
      }
      if (text.includes('justification') || text.includes('not justified') || text.includes('insufficient reason')) {
        return 'Insufficient Justification';
      }
      if (text.includes('policy') || text.includes('violation') || text.includes('compliance')) {
        return 'Policy Violation';
      }
      if (text.includes('schedule') || text.includes('timing') || text.includes('notice')) {
        return 'Scheduling Issues';
      }
      if (text.includes('capacity') || text.includes('resources') || text.includes('staffing')) {
        return 'Resource Constraints';
      }
      if (text.includes('documentation') || text.includes('incomplete') || text.includes('missing')) {
        return 'Incomplete Documentation';
      }
      if (text.includes('approval') || text.includes('authorization') || text.includes('permission')) {
        return 'Unauthorized Request';
      }
      if (text.includes('duplicate') || text.includes('already') || text.includes('existing')) {
        return 'Duplicate Request';
      }
      
      return 'Other';
    };

    const rejectionCounts = rejectedRequests.reduce((acc, request) => {
      const reason = categorizeRejection(request.approver_comment);
      acc[reason] = (acc[reason] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Calculate totals for percentages
    const total = rejectedRequests.length;
    
    return Object.entries(rejectionCounts)
      .filter(([, count]) => count > 0)
      .map(([reason, count]) => ({
        name: reason,
        value: count,
        percentage: ((count / total) * 100).toFixed(1)
      }))
      .sort((a, b) => b.value - a.value); // Sort by count (descending)
  }, [requests]);

  // Convert data for DonutChart component
  const donutChartData = useMemo<DonutChartData[]>(() => {
    return rejectionData.map((item, index) => ({
      name: item.name,
      value: item.value,
      fill: `hsl(var(--chart-${(index % 20) + 1}))`
    }));
  }, [rejectionData]);

  // Create chart config for DonutChart
  const donutChartConfig = useMemo(() => {
    const config: Record<string, { label: string; color?: string }> = {
      value: {
        label: "Rejections",
      },
    };
    
    rejectionData.forEach((item, index) => {
      config[item.name] = {
        label: item.name,
        color: `hsl(var(--chart-${(index % 20) + 1}))`,
      };
    });
    
    return config;
  }, [rejectionData]);

  // Calculate total rejections for center label
  const totalRejections = useMemo(() => {
    return requests?.filter(req => req.status === OvertimeStatus.REJECTED).length || 0;
  }, [requests]);

  // Handle CSV download
  const handleDownload = () => {
    if (!rejectionData.length) return;

    const headers = ["Rejection Reason", "Count", "Percentage"];
    const csvData = rejectionData.map(item => [
      item.name,
      item.value.toString(),
      `${item.percentage}%`
    ]);

    const csvContent = [
      headers.join(","),
      ...csvData.map(row => row.join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `rejection_reasons_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Chart content
  const chartContent = (
    <div className="h-72">
      {isLoading ? (
        <div className="flex items-center justify-center h-full">
          <div className="animate-pulse h-40 w-full bg-muted rounded-lg" />
        </div>
      ) : totalRejections === 0 ? (
        <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
          <XCircle className="h-8 w-8 mb-2 text-muted-foreground" />
          <p className="text-sm font-medium">No rejections found</p>
          <p className="text-xs">Chart will appear when requests are rejected</p>
        </div>
      ) : (
        <DonutChart
          data={donutChartData}
          chartConfig={donutChartConfig}
          isLoading={isLoading}
          showLegend={true}
          showCenterLabel={true}
          centerLabelValue={totalRejections.toString()}
          centerLabelSubtext="Rejected Request"
          formatValue={(value) => `${value} rejections`}
          height={288}
          className="flex items-center justify-center"
        />
      )}
    </div>
  );
    
  useImperativeHandle(ref, () => ({
    download: handleDownload
  }));

  // Render with or without Card container based on standalone prop
  if (standalone) {
    return chartContent;
  }

  return (
    <Card className="shadow-md hover:shadow-lg transition-shadow duration-200 h-[450px] flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 border-b py-3 px-6">
        <CardTitle className="text-lg font-semibold text-left flex items-center">
          <XCircle className="w-4 h-4 mr-2 text-primary" />
          {title}
        </CardTitle>
        <div className="flex items-center gap-2 ml-auto">
          <ExportButton 
            onClick={handleDownload} 
            disabled={!rejectionData.length || isLoading} 
          />
        </div>
      </CardHeader>
      <CardContent className="pt-2 pb-4 flex-1 flex flex-col">
        <div className="h-72 flex-1">
          {donutChartData.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground">
              <PieChart className="w-12 h-12 mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">
                {hasActiveFilters ? "No data matches your filters" : "No rejection reason data available"}
              </p>
              <p className="text-sm max-w-xs">
                {hasActiveFilters 
                  ? "Try adjusting the filters to see data"
                  : "Data will appear here once overtime requests are rejected"
                }
              </p>
            </div>
          ) : (
            <DonutChart
              data={donutChartData}
              chartConfig={donutChartConfig}
              isLoading={isLoading}
              showLegend={true}
              showCenterLabel={true}
              centerLabelValue={totalRejections.toString()}
              centerLabelSubtext="Rejected Requests"
              formatValue={(value) => `${value} rejections`}
              height={288}
              innerRadius={80}
              outerRadius={120}
              className="flex items-center justify-center"
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
});

export default RejectionReasonDonutChart; 